import React, { useCallback, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import { LocalShipping, Storefront } from '@mui/icons-material';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-toastify';

import Loader from '../../components/generic/Loader';
import { useShopDetails } from '../../hooks/use-shop';

/** Same ten seconds the customer's tracking screen polls at. */
const POLL_MS = 10_000;

const ACTIVE_STATUSES = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'OUT_FOR_DELIVERY'];
const FINISHED_STATUSES = ['DELIVERED', 'COMPLETED', 'CANCELLED'];

const LIST_DELIVERY_ORDERS = gql`
	query deliveryOrdersForLocation($locationId: String!, $statuses: [String!]) {
		deliveryOrdersForLocation(locationId: $locationId, statuses: $statuses) {
			id
			reference
			status
			orderType
			paymentMethod
			subtotal
			taxAmount
			deliveryFee
			total
			distanceKm
			estimatedMinutes
			addressSnapshot
			driverName
			cancellationReason
			placedAt
			customerName
			customerPhone
			payment {
				status
				method
				amount
			}
			items {
				id
				productName
				unit
				unitPrice
				quantity
				lineTotal
			}
		}
	}
`;

const UPDATE_STATUS = gql`
	mutation updateDeliveryOrderStatus($orderId: Int!, $status: String!, $driverName: String) {
		updateDeliveryOrderStatus(orderId: $orderId, status: $status, driverName: $driverName) {
			id
			status
			driverName
		}
	}
`;

interface OrderItem {
	id: number;
	productName: string;
	unit: string;
	unitPrice: number;
	quantity: number;
	lineTotal: number;
}

interface DeliveryOrder {
	id: number;
	reference: string;
	status: string;
	orderType: string;
	paymentMethod: string;
	subtotal: number;
	taxAmount: number;
	deliveryFee: number;
	total: number;
	distanceKm?: number | null;
	estimatedMinutes?: number | null;
	addressSnapshot?: string | null;
	driverName?: string | null;
	cancellationReason?: string | null;
	placedAt: string;
	customerName?: string | null;
	customerPhone?: string | null;
	payment?: { status: string; method?: string | null; amount: number } | null;
	items: OrderItem[];
}

/** How the order is being paid, in the words a cashier would use. */
function paymentLabel(order: DeliveryOrder): string {
	if (!order.payment) return 'Cash on delivery';
	const how = order.payment.method ? order.payment.method.toUpperCase() : order.paymentMethod;
	switch (order.payment.status) {
		case 'CAPTURED':
			return `Paid online · ${how}`;
		case 'REFUND_PENDING':
			return 'Refund on its way';
		case 'REFUNDED':
			return 'Refunded';
		case 'REFUND_FAILED':
			return 'Refund failed';
		default:
			return `Online · ${order.payment.status.toLowerCase()}`;
	}
}

/**
 * The single valid next step, mirroring the fork the API validates against.
 *
 * Duplicated deliberately: the server is the authority and still rejects an
 * invalid transition, but offering a dropdown of eight statuses would give
 * staff seven wrong answers and rely on an error message after a click that
 * should never have been possible. If the two ever disagree the server wins.
 */
const NEXT_STEP: Record<string, Record<string, { status: string; label: string }>> = {
	DELIVERY: {
		PENDING: { status: 'CONFIRMED', label: 'Confirm' },
		CONFIRMED: { status: 'PREPARING', label: 'Start preparing' },
		PREPARING: { status: 'READY', label: 'Mark ready' },
		READY: { status: 'OUT_FOR_DELIVERY', label: 'Send out' },
		OUT_FOR_DELIVERY: { status: 'DELIVERED', label: 'Mark delivered' },
	},
	SELF_PICKUP: {
		PENDING: { status: 'CONFIRMED', label: 'Confirm' },
		CONFIRMED: { status: 'PREPARING', label: 'Start preparing' },
		PREPARING: { status: 'READY', label: 'Mark ready' },
		READY: { status: 'COMPLETED', label: 'Mark collected' },
	},
};

/** Cancellable only before the food is made — the same rule the API enforces. */
const CANCELLABLE = ['PENDING', 'CONFIRMED'];

const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
	PENDING: 'warning',
	CONFIRMED: 'primary',
	PREPARING: 'primary',
	READY: 'primary',
	OUT_FOR_DELIVERY: 'primary',
	DELIVERED: 'success',
	COMPLETED: 'success',
	CANCELLED: 'error',
};

function money(value: number): string {
	return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function DeliveryOrderQueuePage() {
	const { locationId, locationName } = useShopDetails();
	const [showFinished, setShowFinished] = useState(false);
	const [dispatching, setDispatching] = useState<DeliveryOrder | null>(null);
	const [driverName, setDriverName] = useState('');

	const statuses = useMemo(
		() => (showFinished ? FINISHED_STATUSES : ACTIVE_STATUSES),
		[showFinished],
	);

	const { data, loading, refetch } = useQuery(LIST_DELIVERY_ORDERS, {
		variables: { locationId, statuses },
		skip: !locationId,
		fetchPolicy: 'cache-and-network',
		pollInterval: POLL_MS,
	});

	const [updateStatus, { loading: updating }] = useMutation(UPDATE_STATUS);

	const orders: DeliveryOrder[] = data?.deliveryOrdersForLocation ?? [];

	const advance = useCallback(
		async (order: DeliveryOrder, status: string, driver?: string) => {
			try {
				await updateStatus({ variables: { orderId: order.id, status, driverName: driver ?? null } });
				toast.success(`${order.reference} → ${status.replace(/_/g, ' ').toLowerCase()}`);
				await refetch();
			} catch (error) {
				toast.error(error instanceof Error ? error.message : 'Could not update the order.');
			}
		},
		[refetch, updateStatus],
	);

	const onAdvance = useCallback(
		(order: DeliveryOrder) => {
			const next = NEXT_STEP[order.orderType]?.[order.status];
			if (!next) {
				return;
			}
			// Dispatch is the only transition needing extra input, so it is the
			// only one that opens a prompt.
			if (next.status === 'OUT_FOR_DELIVERY') {
				setDriverName(order.driverName ?? '');
				setDispatching(order);
				return;
			}
			void advance(order, next.status);
		},
		[advance],
	);

	const confirmDispatch = useCallback(async () => {
		if (!dispatching) {
			return;
		}
		if (!driverName.trim()) {
			toast.error("Enter the driver's name — the customer sees it while tracking.");
			return;
		}
		await advance(dispatching, 'OUT_FOR_DELIVERY', driverName.trim());
		setDispatching(null);
		setDriverName('');
	}, [advance, dispatching, driverName]);

	if (!locationId) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Typography variant="h4" gutterBottom>
					Delivery Orders
				</Typography>
				<Typography color="text.secondary">Choose a location first.</Typography>
			</Container>
		);
	}

	if (loading && orders.length === 0) {
		return <Loader isLoading />;
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
				<Box>
					<Typography variant="h4" gutterBottom>
						Delivery Orders
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{locationName ? `${locationName} — ` : ''}updates every {POLL_MS / 1000} seconds. Move each
						order along as it progresses.
					</Typography>
				</Box>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={showFinished ? 'finished' : 'active'}
					onChange={(_, value) => value && setShowFinished(value === 'finished')}
				>
					<ToggleButton value="active">Active</ToggleButton>
					<ToggleButton value="finished">Finished</ToggleButton>
				</ToggleButtonGroup>
			</Stack>

			{orders.length === 0 ? (
				<Paper sx={{ p: 6, textAlign: 'center' }}>
					<Typography variant="h6" gutterBottom>
						{showFinished ? 'No finished orders' : 'Nothing waiting'}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{showFinished
							? 'Completed and cancelled orders will appear here.'
							: 'New delivery orders show up here as customers place them.'}
					</Typography>
				</Paper>
			) : (
				<Stack spacing={2}>
					{orders.map((order) => {
						const next = NEXT_STEP[order.orderType]?.[order.status];
						const isPickup = order.orderType === 'SELF_PICKUP';
						return (
							<Paper key={order.id} sx={{ p: 3 }}>
								<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
									<Box sx={{ minWidth: 0 }}>
										<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
											<Typography variant="h6">{order.reference}</Typography>
											<Chip
												size="small"
												label={order.status.replace(/_/g, ' ')}
												color={STATUS_COLOR[order.status] ?? 'default'}
											/>
											<Chip
												size="small"
												variant="outlined"
												icon={isPickup ? <Storefront /> : <LocalShipping />}
												label={isPickup ? 'Pick up' : 'Delivery'}
											/>
										</Stack>
										<Typography variant="body2" color="text.secondary">
											{new Date(order.placedAt).toLocaleString('en-IN')}
											{order.customerName ? ` · ${order.customerName}` : ''}
											{order.customerPhone ? ` · ${order.customerPhone}` : ''}
										</Typography>
									</Box>
									<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
										<Typography variant="h6">{money(order.total)}</Typography>
										<Typography
											variant="caption"
											color={order.payment?.status === 'REFUND_FAILED' ? 'error' : 'text.secondary'}
										>
											{paymentLabel(order)}
										</Typography>
									</Box>
								</Stack>

								<Divider sx={{ my: 2 }} />

								<Stack spacing={0.5}>
									{order.items.map((item) => (
										<Stack key={item.id} direction="row" justifyContent="space-between">
											<Typography variant="body2">
												{item.quantity} × {item.productName}
											</Typography>
											<Typography variant="body2">{money(item.lineTotal)}</Typography>
										</Stack>
									))}
								</Stack>

								{order.addressSnapshot ? (
									<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
										Deliver to: {order.addressSnapshot}
										{typeof order.distanceKm === 'number' ? ` (${order.distanceKm.toFixed(1)} km)` : ''}
									</Typography>
								) : null}

								{order.driverName ? (
									<Typography variant="body2" sx={{ mt: 1 }}>
										Driver: {order.driverName}
									</Typography>
								) : null}

								{order.cancellationReason ? (
									<Typography variant="body2" color="error" sx={{ mt: 1 }}>
										{order.cancellationReason}
									</Typography>
								) : null}

								{order.payment?.status === 'REFUND_FAILED' ? (
									<Alert severity="error" sx={{ mt: 2 }}>
										The automatic refund of {money(order.payment.amount)} failed. Refund it from this
										restaurant&apos;s Razorpay dashboard, and quote {order.reference}.
									</Alert>
								) : null}

								{next || CANCELLABLE.includes(order.status) ? (
									<Stack direction="row" spacing={1} sx={{ mt: 2 }}>
										{next ? (
											<Button variant="contained" disabled={updating} onClick={() => onAdvance(order)}>
												{next.label}
											</Button>
										) : null}
										{CANCELLABLE.includes(order.status) ? (
											<Button
												color="error"
												disabled={updating}
												onClick={() => {
													const refundNote =
														order.payment?.status === 'CAPTURED'
															? ` The customer is refunded ${money(order.payment.amount)} in full.`
															: '';
													if (window.confirm(`Cancel ${order.reference}? The stock goes back.${refundNote}`)) {
														void advance(order, 'CANCELLED');
													}
												}}
											>
												Cancel
											</Button>
										) : null}
									</Stack>
								) : null}
							</Paper>
						);
					})}
				</Stack>
			)}

			<Dialog open={dispatching !== null} onClose={() => setDispatching(null)} fullWidth maxWidth="xs">
				<DialogTitle>Send {dispatching?.reference} out</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						autoFocus
						required
						label="Driver name"
						value={driverName}
						onChange={(event) => setDriverName(event.target.value)}
						helperText="The customer sees this on their tracking screen."
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setDispatching(null)} disabled={updating}>
						Cancel
					</Button>
					<Button variant="contained" onClick={() => void confirmDispatch()} disabled={updating}>
						Send out
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default DeliveryOrderQueuePage;
